import { useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "@/shared/lib/hooks/useDebouncedValue";
import { Spinner, Text } from "@/shared/ui";
import { useConversations } from "../hooks/useConversations";
import { groupConversations } from "../lib/groups";
import { ConversationRow } from "./ConversationRow";
import { SearchIcon } from "./icons";

/**
 * The sidebar conversation history: debounced search, recency/favorite grouping,
 * and per-row CRUD. Consumes the list Query hook and ships all three async
 * states (loading / empty / error) — the canonical feature shape.
 */
export function ConversationList() {
  const { t } = useTranslation();
  const params = useParams({ strict: false }) as { id?: string };
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 250);

  const { data, isPending, isError, error, refetch, isFetching } = useConversations(
    debouncedSearch ? { search: debouncedSearch } : {},
  );

  const groups = useMemo(
    () => (data ? groupConversations(data.items, Date.now()) : []),
    [data],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="relative mb-2 flex items-center">
        <SearchIcon className="pointer-events-none absolute left-3 size-[14px] text-ink-faint" />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("conversations.recents")}
          aria-label={t("sidebar.searchChats")}
          className="w-full rounded-md bg-bubble py-1.5 pl-9 pr-3 font-sans text-[13px] text-ink outline-none transition-colors duration-150 ease-house placeholder:text-ink-faint hover:bg-tint focus-visible:bg-tint"
        />
        {isFetching && !isPending ? (
          <Spinner size={14} className="absolute right-3" />
        ) : null}
      </div>

      <div className="-mr-2 min-h-0 flex-1 overflow-y-auto pr-2">
        {isPending ? (
          <Text variant="body" className="px-3 py-2 text-[13px] text-ink-faint">
            {t("chat.loading")}
          </Text>
        ) : isError ? (
          <div className="flex flex-col gap-2 px-3 py-2">
            <Text variant="body" className="text-[13px] text-ink-soft">
              {error.message}
            </Text>
            <button
              type="button"
              onClick={() => void refetch()}
              className="self-start text-[13px] font-medium text-ink underline-offset-2 hover:underline"
            >
              {t("errors.tryAgain")}
            </button>
          </div>
        ) : groups.length === 0 ? (
          <Text variant="body" className="px-3 py-2 text-[13px] text-ink-faint">
            {debouncedSearch
              ? t("conversations.noConversationsMatching", { query: debouncedSearch })
              : t("conversations.noConversationsYet")}
          </Text>
        ) : (
          <nav className="flex flex-col gap-3" aria-label={t("conversations.recents")}>
            {groups.map((group) => (
              <div key={group.key} className="flex flex-col gap-0.5">
                <Text variant="label" as="div" className="px-3 pb-1">
                  {t(group.labelKey)}
                </Text>
                {group.items.map((conversation) => (
                  <ConversationRow
                    key={conversation.id}
                    conversation={conversation}
                    active={params.id === conversation.id}
                  />
                ))}
              </div>
            ))}
          </nav>
        )}
      </div>
    </div>
  );
}
